import { addChoice, rankChoices } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { useState } from "react";
import { fieldValue } from "./fieldValue";

/** How many matches the dropdown lists before it scrolls. */
const DROPDOWN_ROWS = 8;
const ROW_HEIGHT = 34;

const DROPDOWN = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 20,
  marginTop: "4px",
  maxHeight: `${DROPDOWN_ROWS * ROW_HEIGHT}px`,
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #e3e3e3",
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
  padding: "4px 0",
} as const;

const OPTION = {
  display: "block",
  width: "100%",
  height: `${ROW_HEIGHT}px`,
  padding: "0 12px",
  border: "none",
  background: "transparent",
  textAlign: "left",
  font: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

const BROWSE = {
  maxHeight: "240px",
  overflowY: "auto",
  border: "1px solid #e3e3e3",
  borderRadius: "8px",
  padding: "8px",
} as const;

/** `value` with the part matching `query` in bold, so the dropdown shows why each row matched. */
function Highlighted({ value, query }: { value: string; query: string }) {
  const at = query ? value.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return <>{value}</>;
  return (
    <>
      {value.slice(0, at)}
      <strong>{value.slice(at, at + query.length)}</strong>
      {value.slice(at + query.length)}
    </>
  );
}

/**
 * A multi-select over the shop's own values (tags, vendors, product types): the chosen ones as
 * removable chips, a field that autocompletes from every value the shop uses (arrow keys and
 * Enter, or a click), and "Browse all" to see and pick from the whole list. With `freeText`, a
 * value the shop does not use yet can be added too (a tag the merchant is about to give
 * products); without it, only one of `options`.
 */
export function ChoicePicker({
  label,
  details,
  placeholder,
  options,
  chosen,
  freeText,
  noun,
  onChange,
}: {
  label: string;
  details: string;
  placeholder: string;
  options: string[];
  chosen: string[];
  freeText: boolean;
  /** What one option is called, plural, for "Browse all 42 tags". */
  noun: string;
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [browsing, setBrowsing] = useState(false);
  const typed = query.trim();
  const matches = rankChoices(options, chosen, typed);
  const known = options.find((o) => o.toLowerCase() === typed.toLowerCase());
  const addable = freeText ? typed : (known ?? "");
  const open = focused && matches.length > 0;
  const active = Math.min(highlight, matches.length - 1);

  function add(value: string) {
    onChange(addChoice(chosen, value));
    setQuery("");
    setHighlight(0);
  }

  function remove(value: string) {
    onChange(chosen.filter((v) => v !== value));
  }

  function toggle(value: string) {
    const lower = value.toLowerCase();
    if (chosen.some((v) => v.toLowerCase() === lower)) {
      onChange(chosen.filter((v) => v.toLowerCase() !== lower));
    } else add(value);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!matches.length) return;
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setHighlight((active + step + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // A highlighted match wins once the merchant has typed or moved to it; else the typed text.
      const picked = open && (typed || highlight > 0) ? matches[active] : undefined;
      const value = picked ?? addable;
      if (value) add(value);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  }

  const sortedAll = [...options].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );

  return (
    <s-stack gap="small-200">
      <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="end">
        <div style={{ position: "relative" }}>
          <s-text-field
            label={label}
            placeholder={placeholder}
            value={query}
            autocomplete="off"
            onInput={(e) => {
              setQuery(fieldValue(e));
              setHighlight(0);
              setFocused(true);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
          />
          {open && (
            <div role="listbox" aria-label={label} style={DROPDOWN}>
              {matches.map((value, i) => (
                <button
                  key={value}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  tabIndex={-1}
                  style={{ ...OPTION, background: i === active ? "#f1f1f1" : "transparent" }}
                  // Before the field's blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(value);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <Highlighted value={value} query={typed} />
                </button>
              ))}
            </div>
          )}
        </div>
        <s-button disabled={!addable || undefined} onClick={() => add(addable)}>
          Add
        </s-button>
      </s-grid>
      {/* Outside the field, so the Add button lines up with the input rather than this hint. */}
      <s-text color="subdued">{details}</s-text>
      {chosen.length > 0 && (
        <s-stack direction="inline" gap="small-200">
          {chosen.map((value) => (
            <s-clickable-chip
              key={value}
              color="strong"
              removable
              accessibilityLabel={`Remove ${value}`}
              onClick={() => remove(value)}
              onRemove={() => remove(value)}
            >
              {value}
            </s-clickable-chip>
          ))}
        </s-stack>
      )}
      {typed && !freeText && !known && matches.length === 0 && (
        <s-text color="subdued">None of your products use "{typed}".</s-text>
      )}
      {options.length > 0 && (
        <s-stack gap="small-200">
          <s-stack direction="inline">
            <s-button variant="tertiary" onClick={() => setBrowsing(!browsing)}>
              {browsing ? `Hide ${noun}` : `Browse all ${options.length} ${noun}`}
            </s-button>
          </s-stack>
          {browsing && (
            <div style={BROWSE}>
              <s-stack direction="inline" gap="small-200">
                {sortedAll.map((value) => {
                  const picked = chosen.some((v) => v.toLowerCase() === value.toLowerCase());
                  return (
                    <s-clickable-chip
                      key={value}
                      color={picked ? "strong" : undefined}
                      icon={picked ? undefined : "plus"}
                      accessibilityLabel={picked ? `Remove ${value}` : `Add ${value}`}
                      onClick={() => toggle(value)}
                    >
                      {value}
                    </s-clickable-chip>
                  );
                })}
              </s-stack>
            </div>
          )}
        </s-stack>
      )}
    </s-stack>
  );
}
